/**
 * router/routes/servicios.routes.ts
 * Servicios (LOCX) module routes, nested under the tenant AppShell — includes
 * the module's own admin/config screens (admin/locx/*), which live in LOCX's
 * sidebar, not the tenant-wide admin screens (see admin.routes.ts).
 * Split out of router/index.ts (openspec/arquitectura/convencion-modulo.md §4).
 */
import type { RouteRecordRaw } from 'vue-router';
import { depositoRouteName } from '../../modules/compras/deposito/deposito-base.js';
import { depositoRoutes } from './deposito.routes.js';

export const serviciosRoutes: RouteRecordRaw[] = [
  {
    path: 'servicios/orders',
    name: 'service-orders',
    component: () => import('../../views/ServiceOrdersView.vue'),
  },

  // SL-22.1: create flow route (PR2)
  {
    path: 'servicios/orders/new',
    name: 'service-order-new',
    component: () => import('../../views/ServiceOrderCreateView.vue'),
  },

  // FS-40: rental wizard route — must be defined BEFORE the :id route so
  // 'rental' is not captured as an id param. Requires locx:write capability.
  {
    path: 'servicios/orders/rental/new',
    name: 'rental-order-new',
    component: () => import('../../views/RentalWizardView.vue'),
    meta: { requiresCapability: 'locx.servicio.crear' },
  },

  // fix/rental-locx-bm FASE 1.3: "Editar" reopens the rental wizard for an
  // existing DRAFT order — same ordering rule as rental/new above (must be
  // defined BEFORE 'servicios/orders/:id' so 'rental' is not captured as
  // the :id param). Requires locx:write, same guard as rental/new.
  {
    path: 'servicios/orders/rental/:id/edit',
    name: 'rental-order-edit',
    component: () => import('../../views/RentalWizardView.vue'),
    props: true,
    meta: { requiresCapability: 'locx.servicio.editar' },
  },

  // SL-22.2: detail route — placeholder for PR2 redirect; full detail view ships in PR3.
  // Defined here so router.hasRoute('service-order-detail') returns true and
  // ServiceOrderCreateView can redirect to it on success instead of the list.
  {
    path: 'servicios/orders/:id',
    name: 'service-order-detail',
    component: () => import('../../views/ServiceOrderDetailView.vue'),
    props: true,
  },

  // add-locx-daily-report-frontend Slice 2 (SL-64.1, SL-64.2) — daily-report
  // creation, own view (not a Dialog — the form is too large for a modal).
  // More path segments than 'servicios/orders/:id' above, so no ordering
  // ambiguity with it (unlike sibling literal-vs-:id routes at the same depth).
  // Gated on locx:write; the view itself also checks order.dailyReportEnabled.
  {
    path: 'servicios/orders/:id/daily-reports/new',
    name: 'daily-report-new',
    component: () => import('../../views/DailyReportCreateView.vue'),
    meta: { requiresCapability: 'locx.parte.crear' },
  },

  // FS-18: /admin/locx — LOCX field/option config (admin only, locx-config-admin PR2)
  // meta.requiresRole:'admin' — backend roleGuard is the authority (FS-18-B).
  // hideSidebar removed 19 jul 2026: "Config. servicios" moved from the topbar
  // Admin dropdown into the LOCX module's own sidebar (daily-operations screen,
  // not a tenant-wide admin screen) — see SidebarRigx.vue's LOCX admin nav block.
  {
    path: 'admin/locx',
    name: 'admin-locx-config',
    component: () => import('../../views/admin/LocxConfigView.vue'),
    // Service types (locx.catalogo.editar) and their tarifario tab (locx.tarifario.editar), 2026-10-13.
    meta: { requiresCapability: ['locx.catalogo.editar', 'locx.tarifario.editar'] },
  },

  // CAT-STD (2026-07-21): ficha de tipo de servicio — replaces the old
  // side-panel expansion in admin-locx-config with a dedicated route.
  // Literal sibling 'admin/locx/assets-config' below is unaffected (no :id clash).
  {
    path: 'admin/locx/service-types/:id',
    name: 'admin-locx-service-type-detail',
    component: () => import('../../views/admin/ServiceTypeDetailView.vue'),
    props: true,
    meta: { requiresCapability: ['locx.catalogo.editar', 'locx.tarifario.editar'] },
  },

  // locx-permisos-de-trabajo-frontend F10a (task 10a.3, design.md D5 point
  // 1) — meta.requiresCapability, NOT requiresRole:'admin' like the LOCX
  // routes above: the PTW backend guards these with
  // requireCapability('locx.permiso.administrar') (ptw-type.routes.ts),
  // not roleGuard('admin') — the client gate must mirror the real one.
  {
    path: 'admin/locx/ptw-types',
    name: 'admin-locx-ptw-types',
    component: () => import('../../views/admin/PtwTypesView.vue'),
    meta: { requiresCapability: 'locx.permiso.administrar' },
  },

  {
    path: 'admin/locx/ptw-types/:id',
    name: 'admin-locx-ptw-type-detail',
    component: () => import('../../views/admin/PtwTypeDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'locx.permiso.administrar' },
  },

  // locx-permisos-de-trabajo-frontend F10c (task 10c.2) — same
  // meta.requiresCapability posture as the ptw-types routes above:
  // `ptw-catalog-entry.routes.ts` guards with
  // requireCapability('locx.permiso.administrar') for the write side too.
  {
    path: 'admin/locx/ptw-catalogs',
    name: 'admin-locx-ptw-catalogs',
    component: () => import('../../views/admin/PtwCatalogsView.vue'),
    meta: { requiresCapability: 'locx.permiso.administrar' },
  },

  // AOP-7.1: /servicios/assets — asset list (locx:read capability required by backend)
  {
    path: 'servicios/assets',
    name: 'assets',
    component: () => import('../../views/servicios/AssetsView.vue'),
    meta: { requiresCapability: 'locx.activo.leer' },
  },

  // AOP-7.1: /servicios/assets/new — asset create (locx:write capability required by backend)
  {
    path: 'servicios/assets/new',
    name: 'asset-new',
    component: () => import('../../views/servicios/AssetCreateView.vue'),
    meta: { requiresCapability: 'locx.activo.crear' },
  },

  // fleet-qr-compliance deferred item — bulk QR sticker printing (design.md
  // §8.2 "selección múltiple de assets"). Literal sibling, same ordering
  // rule as 'servicios/assets/new' above — must stay before the :id route.
  {
    path: 'servicios/assets/qr-print-bulk',
    name: 'assets-qr-print-bulk',
    component: () => import('../../views/AssetsQrBulkPrintView.vue'),
    meta: { requiresCapability: 'locx.activo.leer' },
  },

  // HANDOFF-DOCUMENTOS.md second pass: /servicios/assets/:id — asset detail (Documentos
  // tab). Literal siblings 'servicios/assets/new' and 'servicios/assets/qr-print-bulk'
  // above MUST stay before this :id route so they aren't captured as an id param
  // (same rule as rrhh/persons/new vs /:id).
  {
    path: 'servicios/assets/:id',
    name: 'asset-detail',
    component: () => import('../../views/servicios/AssetDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'locx.activo.leer' },
  },

  // locx-permisos-de-trabajo-frontend F1 (task 1.6, design.md D5) —
  // Permisos de Trabajo (PTW): dashboard+listado, alta, detalle.
  // requiresCapability is any-of (D5 table) — dashboard/listado/detail
  // must stay reachable by a `firmar`-only account (a shift signer who
  // never gets to transition or approve anything), same pattern as
  // /compras/aprobaciones' array gate (2026-08-31-requi-aprobacion-oc-
  // por-monto). 'servicios/permisos/nuevo' is a literal sibling and MUST
  // stay registered before the ':id' route below (same ordering rule as
  // servicios/assets/new vs :id above).
  {
    path: 'servicios/permisos',
    name: 'permisos',
    component: () => import('../../views/servicios/PermisosView.vue'),
    meta: { requiresCapability: ['locx.permiso.leer', 'locx.permiso.firmar'] },
  },

  {
    path: 'servicios/permisos/nuevo',
    name: 'permiso-new',
    // F3b-ii (task 3b-ii.3): dedicated create view, replacing the F1
    // placeholder now that PtwDynamicForm (F3b-i) exists to render it.
    component: () => import('../../views/servicios/PermisoCreateView.vue'),
    meta: { requiresCapability: 'locx.permiso.crear' },
  },

  {
    path: 'servicios/permisos/:id',
    name: 'permiso-detail',
    // F4a (task 4a.5): dedicated ficha view, replacing the F1 placeholder
    // now that PermisoDetailView (EntityHeader + Tabs + PtwFieldValuesPanel)
    // exists to render it.
    component: () => import('../../views/servicios/PermisoDetailView.vue'),
    props: true,
    meta: { requiresCapability: ['locx.permiso.leer', 'locx.permiso.firmar'] },
  },

  // locx-permisos-de-trabajo-frontend F11 (task 11.3, design.md D8) — ATS
  // listado. Baseline capability is `locx.permiso.leer` alone (matches
  // `ats.routes.ts`'s own GET guard) — unlike /servicios/permisos, ATS
  // has no `firmar` concept to combine with.
  {
    path: 'servicios/ats',
    name: 'ats',
    component: () => import('../../views/servicios/AtsView.vue'),
    meta: { requiresCapability: 'locx.permiso.leer' },
  },

  // kernel-cross-tenant-service-write Phase 7 (task 7.4) — owner-side
  // inbox for cross-tenant `service_order_request` rows. requiredCapability
  // mirrors the 4 HTTP routes exactly (Phase 6 — no new capability string).
  {
    path: 'servicios/service-order-requests',
    name: 'service-order-requests-inbox',
    component: () => import('../../views/servicios/ServiceOrderRequestInboxView.vue'),
    meta: { requiresCapability: 'locx.servicio.crear' },
  },

  // locx-ats-content-and-lifecycle-frontend F2 (task 2.13, design.md FD4)
  // — dedicated alta route (replaces the old inline `ActionPanel` on
  // AtsView.vue). 'servicios/ats/nuevo' is a literal sibling and MUST
  // stay registered before the ':id' route below (same ordering rule as
  // servicios/permisos/nuevo vs :id / servicios/assets/new vs :id above).
  {
    path: 'servicios/ats/nuevo',
    name: 'ats-new',
    component: () => import('../../views/servicios/AtsCreateView.vue'),
    meta: { requiresCapability: 'locx.permiso.crear' },
  },

  // locx-ats-content-and-lifecycle-frontend F1 (task 1.12) — read-only ATS
  // ficha in F1; F2 leaves the route/component unchanged (AtsDetailView.vue
  // stays read-only until F3 wires its edit branch, tasks.md 2.17).
  {
    path: 'servicios/ats/:id',
    name: 'ats-detail',
    component: () => import('../../views/servicios/AtsDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'locx.permiso.leer' },
  },

  // 2026-07-21: /servicios/fleet-failures — fleet-wide fallas report for the
  // operations manager. requiresCapability mirrors the backend's scoping
  // gate exactly (locx:fleet-failure-manage, NOT locx:read) — without it
  // GET /servicios/fleet-failures silently narrows to the caller's own
  // fallas instead of 403ing, which would make this page render but lie.
  {
    path: 'servicios/fleet-failures',
    name: 'fleet-failures-report',
    component: () => import('../../views/servicios/FleetFailuresReportView.vue'),
    meta: { requiresCapability: 'locx.falla.gestionar-ajenas' },
  },

  // 2026-07-22: /servicios/fleet-failure-reports — the informe-level
  // master grid (legacy bramix-rrhh's primary screen: "Datos Generales"
  // + nested fallas), separate from the falla-flat worklist above.
  // requiresCapability locx:read (same gate as the backend's own list
  // route).
  {
    path: 'servicios/fleet-failure-reports',
    name: 'fleet-failure-reports',
    component: () => import('../../views/servicios/FleetFailureReportsView.vue'),
    meta: { requiresCapability: 'locx.falla.leer' },
  },

  // Literal sibling MUST stay registered before the ':id' route below
  // (same ordering rule as servicios/assets/new vs :id above) — not
  // actually at risk here since there's no literal 'new' path segment
  // under fleet-failure-reports on the frontend router.
  {
    path: 'servicios/fleet-failure-reports/:id',
    name: 'fleet-failure-report-detail',
    component: () => import('../../views/servicios/FleetFailureReportDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'locx.falla.leer' },
  },

  // 23 jul 2026: /servicios/fuel-loads — "Informe de combustible" admin
  // report. The backend route + apps/operator field capture already
  // existed; this office-side read-only grid was the missing piece
  // (user report). requiresCapability locx:read (same gate as the
  // backend's own GET list route).
  {
    path: 'servicios/fuel-loads',
    name: 'fuel-loads-report',
    component: () => import('../../views/servicios/FuelLoadsReportView.vue'),
    meta: { requiresCapability: 'locx.parte.leer' },
  },

  // fleet-qr-compliance PR6 (23 jul 2026): /servicios/fleet-compliance —
  // fleet-wide compliance grid (semáforo). requiresCapability locx:read,
  // same gate as the backend's GET /servicios/assets/fleet-compliance.
  {
    path: 'servicios/fleet-compliance',
    name: 'fleet-compliance',
    component: () => import('../../views/servicios/FleetComplianceView.vue'),
    meta: { requiresCapability: 'locx.activo.leer' },
  },

  // odd/tasks/compras-deposito.md S1 — the Depósito screens, mounted here AND
  // under /compras/deposito (deposito.routes.ts: same components, same per-tab
  // capabilities, own route names), so a LOCX-only tenant keeps it inside
  // Servicios. The old /servicios/stock inventory is folded into its home; the
  // old recepcion/despacho tab URLs land on the renamed tabs.
  ...depositoRoutes('servicios', [
    { path: 'recepcion', redirect: { name: depositoRouteName('servicios', 'recibir') } },
    { path: 'despacho', redirect: { name: depositoRouteName('servicios', 'entregar') } },
  ]),
  { path: 'servicios/stock', redirect: { name: depositoRouteName('servicios', 'inicio') } },

  // "Mis solicitudes" (LOCX) — openspec/auditorias/2026-08-26-kernel-
  // capabilities-permisos.md §2 item 9. requiresCapability
  // requi.solicitud.leer-propias, same gate as the backend's GET
  // /procurement/my-cases (compras' own endpoint — this view lives in
  // the LOCX section but calls compras' HTTP surface directly, same as
  // the LOCX gerente already does today for "Solicitar compra").
  {
    path: 'servicios/mis-solicitudes',
    name: 'servicios-mis-solicitudes',
    component: () => import('../../views/servicios/MyRequestsView.vue'),
    meta: { requiresCapability: 'requi.solicitud.leer-propias' },
  },

  // Alias LOCX de compras/nueva (mismo componente) — existe SOLO para que
  // el "+ Nueva solicitud" de Mis Solicitudes no navegue a un path
  // /compras/*, que le cambiaría el chrome del sidebar a REQUI completo
  // (active-module.ts resuelve el módulo activo por prefijo de URL, sin
  // mirar capabilities — un solicitante LOCX terminaba viendo pipeline/
  // aprobaciones/OC solo por la URL). El componente no lee route.name,
  // así que reusarlo acá es seguro; compras-new queda intacto para el
  // comprador real. Literal ANTES de servicios/mis-solicitudes/:id, mismo
  // motivo que el comentario de esa ruta.
  {
    path: 'servicios/mis-solicitudes/nueva',
    name: 'servicios-mis-solicitudes-nueva',
    component: () => import('../../views/compras/ComprasCaseCreateView.vue'),
    meta: { requiresCapability: 'requi.solicitud.crear' },
  },

  // locx-supervisor-catalogo-nav (SL-91, design.md D7) — LOCX alias of the
  // recruitment-request detail, SAME mechanism as 'servicios-mis-
  // solicitudes-nueva' above: opening a recruitment request's detail from
  // the "Reclutamiento" tab of "Mis Solicitudes" must NOT navigate under
  // /rrhh/*, which would flip the sidebar's active module by URL prefix
  // (active-module.ts). The component doesn't read route.name, so reusing
  // it here is safe — 'recruitment-request-detail' (/rrhh/recruitment-
  // requests/:id) stays intact for nomi.rrhh. Registered literal-BEFORE
  // 'servicios/mis-solicitudes/:id' by the same convention as 'nueva'
  // above (2 segments vs. 1, so it wouldn't actually collide, but this
  // keeps every LOCX-scoped literal sibling grouped together).
  {
    path: 'servicios/mis-solicitudes/reclutamiento/:id',
    name: 'servicios-mis-solicitudes-reclutamiento-detail',
    component: () => import('../../views/rrhh/RecruitmentRequestDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'nomi.busqueda.solicitar' },
  },

  // Detalle de UNA SA propia — mismo guard que la lista de arriba. Ruta
  // hija literal-antes-que-:id no aplica acá salvo por 'nueva' arriba,
  // que ya quedó registrada primero.
  {
    path: 'servicios/mis-solicitudes/:id',
    name: 'servicios-mis-solicitudes-detail',
    component: () => import('../../views/servicios/MyRequestDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'requi.solicitud.leer-propias' },
  },

  // add-locx-dashboard (24 jul 2026): /servicios — LOCX "Dashboard" landing
  // view, mirrors rrhh-home's shape. requiresCapability locx:read, same gate
  // as the backend's GET /servicios/dashboard. Literal sibling
  // 'servicios/orders' etc. above are unaffected — this is a plain top-level
  // literal segment, not a shared :id prefix.
  {
    path: 'servicios',
    name: 'servicios-home',
    component: () => import('../../views/servicios/ServiciosHomeView.vue'),
    meta: { requiresCapability: 'locx.servicio.leer' },
  },

  // add-locx-trip-fuel-tracking PR3 (2026-07-22): /servicios/vehicle-trips
  // — "Viajes" approval queue (apps/frontend admin only; the field
  // operator only creates/views its own requests in apps/operator, never
  // approves — design.md §7). requiresCapability locx:write, same gate as
  // the backend's PATCH transition route. Literal sibling
  // 'servicios/vehicle-trips' MUST stay before the ':id' route below
  // (same ordering rule as servicios/assets/new vs :id above).
  {
    path: 'servicios/vehicle-trips',
    name: 'vehicle-trips-list',
    component: () => import('../../views/servicios/VehicleTripsListView.vue'),
    meta: { requiresCapability: 'locx.viaje.aprobar' },
  },

  {
    path: 'servicios/vehicle-trips/:id',
    name: 'vehicle-trip-detail',
    component: () => import('../../views/servicios/VehicleTripDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'locx.viaje.aprobar' },
  },

  // AAU-10: /admin/locx/assets-config — LOCX asset-type/attribute-def config.
  // locx-supervisor-catalogo-nav (SL-90, D6): meta.requiresRole:'admin' →
  // meta.requiresCapability:'locx.catalogo.editar' — mirrors the backend
  // guard (asset-type-admin.routes.ts / asset-attribute-def-admin.routes.ts,
  // AZ-17.1). admin keeps access via the '*' wildcard (guard.ts satisfiesOne).
  // hideSidebar removed 19 jul 2026 — see admin-locx-config above, same rationale.
  {
    path: 'admin/locx/assets-config',
    name: 'admin-locx-assets-config',
    component: () => import('../../views/admin/AssetConfigView.vue'),
    meta: { requiresCapability: 'locx.catalogo.editar' },
  },

  // CAT-STD (2026-07-21): ficha de tipo de activo — replaces the old
  // side-panel expansion in admin-locx-assets-config with a dedicated route.
  // locx-supervisor-catalogo-nav (SL-90, D6): same conversion as above —
  // consumes asset-attribute-def-admin.routes.ts, AZ-17.1.
  {
    path: 'admin/locx/asset-types/:id',
    name: 'admin-locx-asset-type-detail',
    component: () => import('../../views/admin/AssetTypeDetailView.vue'),
    props: true,
    meta: { requiresCapability: 'locx.catalogo.editar' },
  },

  // 2026-07-21: /admin/locx/failure-types-config — LOCX failure-type
  // catalog admin (closes a prod gap: the "tipo de falla" field was
  // permanently empty with no way to populate it). No detail ficha —
  // label editing is inline in FailureTypeManager.vue.
  // locx-supervisor-catalogo-nav (SL-90, D6): meta.requiresRole:'admin' →
  // meta.requiresCapability:'locx.catalogo.editar' — mirrors
  // failure-type-admin.routes.ts (AZ-17.1).
  {
    path: 'admin/locx/failure-types-config',
    name: 'admin-locx-failure-types-config',
    component: () => import('../../views/admin/FailureTypeConfigView.vue'),
    meta: { requiresCapability: 'locx.catalogo.editar' },
  },

  // Vehicle document type editor (name, expiry, presentation, warning days) —
  // guarded like vehicle-document-type-admin.routes.ts. Also linked from NOMI → Catálogos.
  {
    path: 'admin/locx/vehicle-document-types-config',
    name: 'admin-locx-vehicle-document-types-config',
    component: () => import('../../views/admin/VehicleDocumentTypeConfigView.vue'),
    meta: { requiresCapability: 'locx.catalogo.editar' },
  },

  // fleet-qr-compliance PR5 — checklist_template admin (versioned: POST
  // always creates a new version, see CreateChecklistTemplateUseCase).
  {
    path: 'admin/locx/checklist-templates-config',
    name: 'admin-locx-checklist-templates-config',
    component: () => import('../../views/admin/ChecklistTemplatesConfigView.vue'),
    meta: { requiresRole: 'admin' },
  },
];
